import { defineComponent as _defineComponent } from 'vue'
import { watch, computed, ref } from 'vue';
import { useI18n } from '@/composables/use-i18n';
import PtNodata from '@/components/PtNodata.vue';
import { Icon as PtIcon } from 'patsnap-biz';
import CompareSimilarTag from './CompareSimilarTag.vue';
import { useCompareInit } from '@/composables/use-compare-init';
import { useCompareUrlState } from '@/composables/use-compare-url-state';
import { useCompareSource } from '@/composables/use-compare-source';
import type { ApiGetCompareOverviewRequest } from '@/infrastructure/api-functions/core-search-api/core-search-api';
import '@patsnap-ui/icon/assets/solid/LoadingBars.svg';
import _ from 'lodash';
import { useTranslationStates } from '@/composables/use-translation-states';
import { useSimilarParagraph } from '@/composables/use-similar-paragraph';
import { useSimilarParaScroll } from '@/composables/use-similar-para-scroll';
import { LangTypeMode } from '@/domains/translation/enums';
import { DEFAULT_LANGUAGE } from '@/domains/translation/constants';
import '@patsnap-ui/icon/assets/solid/DoubleArrowDown.svg';

interface DisplayResultList {
    src: {
        PN: string;
        similar_part: string;
        field_type: string;
    };
    target: {
        PN: string;
        similar_part: string;
        field_type: string;
    }[];
    similarity: number;
    part_id: number;
    paragraph_id: string;
    showAll: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CompareResultList',
  setup(__props) {

const {
    isLoading,
    isNodata,
    isSame,
    isTimeout,

    deferred,

    compareResultList,
    textCompareLang,
    tgtPN,

    compareInit,
    resetCompareInitState,
    performFakeComparison,
} = useCompareInit();

const {
    compareId,
    leftId,
    rightId,
    isTargetEmpty,
    isSourceCustom,
    isSourceCustomText,
    isSourcePatent,
} = useCompareUrlState();

const { compareResultTextList } = useCompareSource();

const {
    switchTranslateLanguage,
    currentTranslateLanguages,
} = useTranslationStates();

const { t } = useI18n();

const { scrollToSimilarPara } = useSimilarParagraph();

const {
    changeSimilarParaNumSerialIdx,
    changeSimilarParaNum,
    getCurScrollInfo,
} = useSimilarParaScroll();

const DEFAULT_RESULT_LENGTH = 1;

// 两侧语言以左侧主专利为准
const currentPatentLang = computed(() => {
    return _.get(currentTranslateLanguages.value, 'left');
});

const displayResultList = ref<DisplayResultList[]>();
watch(compareResultList, () => {
    displayResultList.value = _.map(compareResultList.value, item => {
        return {
            ...item,
            showAll: item.target.length <= DEFAULT_RESULT_LENGTH,
        };
    });
}, { immediate: true });

const isTargetPatentChange = ref(false);

const scrollToSimilar = ({
    num,
    index,
}: {
    num: string;
    index: number;
}) => {
    changeSimilarParaNum(num);
    changeSimilarParaNumSerialIdx(index);
    scrollToSimilarPara(getCurScrollInfo('left'));
    scrollToSimilarPara(getCurScrollInfo('right'));
};

// 只有左侧leftId为default或是专利id默认为语义检索文本过来的，需要执行compareInit
watch(rightId, async () => {
    // 重置所有状态参数
    resetCompareInitState();
    switchTranslateLanguage({
        side: 'both',
        lang: DEFAULT_LANGUAGE,
        langType: LangTypeMode.DEFAULT,
    });
    if (isTargetEmpty.value || isSourceCustom.value || isSame.value) {
        if (isSame.value) {
            await performFakeComparison();
        }
        if (!isTargetEmpty.value) {
            /*
            * 未执行compare-over的情况，需要手动resolve
            */
            deferred.value?.resolve({});
        }
        return;
    }
    isTargetPatentChange.value = true;
    // 右侧专利切换时，需要重置当前的翻译状态
    const params:ApiGetCompareOverviewRequest = {
        compare_id: compareId.value,
        target_patent_id: rightId.value,
    };
    if (isSourcePatent.value) {
        params.src_patent_id = leftId.value;
    }

    if (isSourceCustomText.value) {
        params.tech_text_list = compareResultTextList.value;
    }

    await compareInit(params);
    isTargetPatentChange.value = false;
}, { immediate: true });


// 当切换翻译时，需要重新调用compare init接口
watch(currentPatentLang, async () => {
    if (isTargetPatentChange.value) return;
    const params:ApiGetCompareOverviewRequest = {
        compare_id: compareId.value,
        target_patent_id: rightId.value,
        src_patent_id: leftId.value,
        text_compare_lang: textCompareLang.value,
    };
    if (currentPatentLang.value && currentPatentLang.value !== DEFAULT_LANGUAGE) {
        params.compare_lang = currentPatentLang.value;
    }
    await compareInit(params);
});


return { __sfc: true,isLoading, isNodata, isSame, isTimeout, deferred, compareResultList, textCompareLang, tgtPN, compareInit, resetCompareInitState, performFakeComparison, compareId, leftId, rightId, isTargetEmpty, isSourceCustom, isSourceCustomText, isSourcePatent, compareResultTextList, switchTranslateLanguage, currentTranslateLanguages, t, scrollToSimilarPara, changeSimilarParaNumSerialIdx, changeSimilarParaNum, getCurScrollInfo, DEFAULT_RESULT_LENGTH, currentPatentLang, displayResultList, isTargetPatentChange, scrollToSimilar, PtNodata, PtIcon, CompareSimilarTag }
}

})